import { formatDefaultPrice } from '@/lib/price-formatter';
import { cn } from '@/lib/utils/generalUtils';
import { LockOpenIcon, PlusIcon } from '@heroicons/react/outline';
import { CheckCircleIcon } from '@heroicons/react/solid';
import Image from 'next/image';
import { useContext } from 'react';
import CartContext from '../CartContext';
import Badge from '../badge';
import {
  DomainAuthorityRankingLabel,
  ESTTurnaroundTimeLabel,
  IsDoFollowLabel,
  NewsLabel,
  SponsorLabel2,
} from '../labels';
import PublicationCategories from '../publications/publicationCategories';

const ManageServices = ({ data }) => {
  const { handleAddItem, canViewPricing } = useContext(CartContext);

  return (
    <>
      {data?.map(({ id: serviceId, attributes: service }) => {
        const products = service?.products?.data;
        const tags = service?.tags?.data;
        const packagePrice = products?.reduce(
          (acc, curr) => (acc += curr?.attributes?.price?.unit_amount / 100),
          0
        );

        return (
          <div
            className="rounded-xl flex flex-col justify-between p-6 bg-white"
            key={serviceId}
          >
            {products?.length > 1 ? (
              <div>
                <div className="flex items-start flex-wrap gap-6 justify-between">
                  <div>
                    <p className="text-2xl font-semibold max-w-60">
                      {service?.name}
                    </p>
                    <ServiceCategories
                      category={service?.category?.data?.attributes?.name}
                      tags={tags}
                    />
                    <p></p>
                  </div>
                  <div className="flex items-center gap-4">
                    <p className="text-xl font-semibold my-4">
                      {formatDefaultPrice(packagePrice)}
                    </p>
                    <button
                      type="button"
                      className="button !py-3"
                      onClick={() =>
                        handleAddItem({
                          id: serviceId,
                          name: service?.name,
                          price: packagePrice,
                          type: 'SERVICE',
                        })
                      }
                    >
                      {canViewPricing ? (
                        <>
                          <PlusIcon className="icon-sm mr-2" />
                          Add to cart
                        </>
                      ) : (
                        <>
                          <LockOpenIcon
                            className="icon-sm mr-2"
                            aria-hidden="true"
                          />
                          Access
                        </>
                      )}
                    </button>
                  </div>
                </div>
                <p className="mb-2 mt-4 font-semibold">Package includes:</p>
                <div
                  className={cn(
                    'grid gap-3 grid-cols-[repeat(auto-fit,minmax(350px,_1fr))]'
                  )}
                >
                  {products?.map(({ id, attributes: product }) => {
                    const publication = product?.publication;
                    return (
                      <div
                        className="space-y-4 flex-1 rounded-3xl border border-neutral-200 px-5 py-8"
                        key={id}
                      >
                        {product?.image?.length > 0 && (
                          <div className="flex-shrink-0">
                            <Image
                              className="object-contain rounded-full"
                              src={product?.image[0]}
                              alt={product.name}
                              width={44}
                              height={44}
                            />
                          </div>
                        )}

                        {/* PUBLICATION DETAILS */}

                        <div>
                          <p className="font-bold text-gray-700">
                            {product?.name}{' '}
                          </p>

                          <div className="flex flex-col sm:flex-row items-start md:items-center gap-2">
                            <a
                              className="text-gray-500"
                              href={product?.publication?.website_url}
                              target="_blank"
                              rel="noreferrer"
                            >
                              <p className="break-words w-full md:w-full max-w-[250px] md:max-w-[auto]">
                                {product?.publication?.website_url}
                              </p>
                            </a>
                          </div>
                        </div>
                        {publication?.publication_categories?.length > 0 && (
                          <PublicationCategories
                            categories={publication?.publication_categories}
                          />
                        )}

                        {publication?.turnaround_time && (
                          <div className="text-sm text-gray-500  ">
                            <ESTTurnaroundTimeLabel
                              turnaroundTime={publication?.turnaround_time}
                            />
                          </div>
                        )}

                        {publication?.domain_authority &&
                          publication?.domain_ranking && (
                            <DomainAuthorityRankingLabel
                              domainAuthority={publication?.domain_authority}
                              domainRanking={publication?.domain_ranking}
                            />
                          )}
                        <div className="flex items-center gap-1 flex-wrap">
                          {publication?.is_do_follow && (
                            <IsDoFollowLabel
                              isDoFollow={publication?.is_do_follow}
                              doFollowLinksAllowed={
                                publication?.do_follow_links_allowed
                              }
                            />
                          )}
                          {publication?.is_sponsored && (
                            <SponsorLabel2
                              isSponsored={publication?.is_sponsored}
                            />
                          )}
                          {publication?.news && (
                            <NewsLabel news={publication?.news} />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <>
                {products?.map(({ id, attributes: product }) => {
                  const price = product?.price;
                  const description =
                    product?.description || service?.description;
                  return (
                    <div className="space-y-4 flex-1" key={id}>
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-2xl font-semibold max-w-60">
                            {product?.name}
                          </p>
                          <ServiceCategories
                            category={service?.category?.data?.attributes?.name}
                            tags={tags}
                          />
                          <p></p>
                        </div>
                        <div className="flex items-center gap-4">
                          <p className="text-xl font-semibold my-4">
                            {formatDefaultPrice(price?.unit_amount / 100)}{' '}
                            <span className="text-sm">
                              {price?.type === 'recurring' ? '/month' : null}
                            </span>
                          </p>
                          <button
                            type="button"
                            className="button !py-3"
                            onClick={() =>
                              handleAddItem({
                                id: serviceId,
                                name: product?.name,
                                price: product?.price?.unit_amount / 100,
                                type: 'SERVICE',
                              })
                            }
                          >
                            {canViewPricing ? (
                              <>
                                <PlusIcon className="icon-sm mr-2" />
                                Add to cart
                              </>
                            ) : (
                              <>
                                <LockOpenIcon
                                  className="icon-sm mr-2"
                                  aria-hidden="true"
                                />
                                Access
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                      <div className="mb-2 mt-4">
                        {description?.startsWith('Includes') ? (
                          <>
                            <span className="font-semibold mb-2 inline-block">
                              Includes:
                            </span>
                            {description
                              .replace('Includes:', '')
                              .split(',')
                              .map((item, index) => (
                                <div key={index} className="flex items-center">
                                  <CheckCircleIcon className="h-5 w-5 text-primary mr-2" />
                                  <span>{item.trim()}</span>
                                </div>
                              ))}
                          </>
                        ) : (
                          <p>{description}</p>
                        )}
                        {/* {description} */}
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        );
      })}
    </>
  );
};

export default ManageServices;

// function serviceCategories({mainCategory,}) {
//   return (
//     <div className="flex items-center gap-x-1 gap-y-2 flex-wrap mt-3">
//       <Badge  className="bg-secondary-100 text-secondary-600">
//         {service?.category?.data?.attributes?.name}
//       </Badge>
//       {tags?.length > 0 &&
//         tags?.map(({ id, attributes: t }) => (
//           <Badge key={id} className="bg-neutral-100 text-neutral-900">
//             {t?.name}
//           </Badge>
//         ))}
//     </div>
//   );
// }

function ServiceCategories({ category, tags }) {
  return (
    <div className="flex items-center gap-x-1 gap-y-2 flex-wrap mt-3">
      <Badge className="bg-secondary-100 text-secondary-600">{category}</Badge>
      {tags?.length > 0 &&
        tags?.map(({ id, attributes: t }) => (
          <Badge key={id} className="bg-neutral-100 text-neutral-900">
            {t?.name}
          </Badge>
        ))}
    </div>
  );
}
